// Mounts the desktop local capture producer for one workspace (one user and tenant) and keeps it fed
// with server state and engine tracks. Inert outside the desktop shell. See call/desktopCapture.ts.
import type { Call, CallCapture, Capability } from '@bcio-voice/contracts';
import { useEffect, useMemo, useState } from 'react';
import type { DesktopBridge } from '../desktop/bridge.ts';
import type { CallBridge } from './bridge.ts';
import type { CallMap } from './callStore.ts';
import type { CaptureRegistry } from './capture.ts';
import {
  AuthGeneration, type CaptureModule, DesktopCaptureWiring, LocalCaptureStore, captureAuthOf, captureBridgeOf, loadCaptureModule,
  randomGenerationPrefix,
} from './desktopCapture.ts';

export interface DesktopCaptureArgs {
  desktop: DesktopBridge | null;
  bridge: CallBridge;
  captures: CaptureRegistry;
  tenantId: string;
  userId: string;
  calls: CallMap;
  liveCalls: Call[];
  capability: Capability | null;
  /** Live updates are in sync and media access is settled. */
  verified: boolean;
  loadModule?: () => Promise<CaptureModule>;
}

export function useDesktopCapture(args: DesktopCaptureArgs): LocalCaptureStore | null {
  const { desktop, bridge, captures, tenantId, userId, calls, liveCalls, capability, verified } = args;
  const captureBridge = useMemo(() => captureBridgeOf(desktop), [desktop]);
  const store = useMemo(() => (captureBridge ? new LocalCaptureStore() : null), [captureBridge]);
  const [wiring, setWiring] = useState<DesktopCaptureWiring | null>(null);

  // auth_generation for this workspace instance (new prefix per user/tenant, counter per auth change).
  const generation = useMemo(() => (captureBridge ? new AuthGeneration(randomGenerationPrefix()) : null), [captureBridge]);
  const [authGeneration, setAuthGeneration] = useState(() => generation?.value ?? '');
  useEffect(() => {
    if (!desktop || !generation) return;
    const observe = (state: { status: Parameters<AuthGeneration['observe']>[0]['status'] }) => {
      if (generation.observe(state)) setAuthGeneration(generation.value);
    };
    let live = true;
    void desktop.auth.state().then(s => { if (live) observe(s); }, () => undefined);
    const off = desktop.auth.onChange(observe);
    return () => { live = false; off(); };
  }, [desktop, generation]);

  // One producer per workspace; created only inside the desktop shell.
  const loadModule = args.loadModule ?? loadCaptureModule;
  useEffect(() => {
    if (!desktop || !captureBridge || !store) return;
    let live = true;
    let created: DesktopCaptureWiring | null = null;
    let offState: (() => void) | null = null;
    loadModule().then(mod => {
      if (!live) return;
      const producer = mod.createProducer({ capture: captureBridge, auth: captureAuthOf(desktop) });
      created = new DesktopCaptureWiring(mod, producer);
      store.set({ status: 'ready', producer: producer.state });
      offState = producer.onStateChange(state => store.set({ status: 'ready', producer: state }));
      setWiring(created);
    }, () => { if (live) store.set({ status: 'error', producer: null }); });
    return () => {
      live = false;
      offState?.();
      setWiring(null);
      void created?.dispose();
    };
    // The loader is fixed for the provider's lifetime.
  }, [desktop, captureBridge, store]);

  // The followed call: the live connected call, kept after it ends until its final state is applied.
  const connected = liveCalls.find(c => c.state === 'active' || c.state === 'held') ?? null;
  const [followed, setFollowed] = useState<string | null>(null);
  useEffect(() => {
    if (connected && connected.id !== followed) setFollowed(connected.id);
  }, [connected?.id, followed]);
  const call = followed ? calls.get(followed) ?? null : null;

  // Server CallCapture for the followed internal call, read only while the capability could allow capture.
  const wantCapture = !!wiring && !!call && call.direction === 'internal' && (call.state === 'active' || call.state === 'held') && capability?.status === 'enabled' && capability.allowed;
  const [capture, setCapture] = useState<CallCapture | null>(null);
  useEffect(() => {
    if (!wantCapture || !call) { setCapture(null); return; }
    const id = call.id;
    const controller = captures.acquire(id);
    const read = () => setCapture(controller.getSnapshot().capture);
    read();
    const off = controller.subscribe(read);
    return () => { off(); captures.release(id); };
  }, [wantCapture, call?.id, captures]);

  useEffect(() => {
    wiring?.update({ tenantId, userId, authGeneration, call, capture, capability, verified });
    if (call && (call.state === 'ended' || call.state === 'failed') && !connected) setFollowed(null);
  }, [wiring, tenantId, userId, authGeneration, call, capture, capability, verified, connected]);

  // Engine tracks (attach, replace, end) go to the producer, which only listens to them.
  useEffect(() => {
    if (!wiring || !bridge.onTracks) return;
    return bridge.onTracks(update => wiring.tracks(update));
  }, [wiring, bridge]);

  return store;
}
